import { useEffect, useRef, useState } from 'react';
import { getChartTitle } from '@/shared/copy/charts';
import { getErrorCopy } from '@/shared/copy/errors';
import { s, spacing } from '@/theme/tokens';
import { StatusBanner } from '@/ui';
import { CHART_EMPTY_STATE_NO_DATA_LABEL, ChartEmptyState } from '@/ui/composites';
import { Box, PieChart, Text } from '@/ui/primitives';
import { chartLoadErrorBannerSx } from './chartLoadErrorBannerSx';
import { ChartPanelTitle } from './ChartPanelTitle';
import { MetricsChartPanelFrame } from './MetricsChartPanelFrame';
import { type StackedMetricsMiniPanelsLayout } from './StackedMetricsStatusChartSection.layout';
import { renderStatusPieLoadingState } from './StackedMetricsStatusChartSection.states';
import type { StatusPieDatum } from './StackedMetricsStatusChartSection.panelTypes';
import type { ReactElement } from 'react';

type StatusPieBreakdownPanelProps = {
  loading: boolean;
  showPlaceholder?: boolean;
  showErrorState: boolean;
  piePanelHeight: number;
  pieChartVisualHeight: number;
  miniPanels: StackedMetricsMiniPanelsLayout;
  headerToChartGap: number;
  miniMetricEmptyTitle: string;
  statusPieData: StatusPieDatum[];
  statusLegend: ReactElement | null;
  legendMarginTop?: string;
};

const runCountFormatter = new Intl.NumberFormat('en-GB');
const statusLegendRowGapPx = spacing.xs;
const statusLegendRowHeightPx = 24;
const statusPieCentreLabelOffsetY = s('none');
const statusPieFallbackChartHeight = 220;
const statusPieHorizontalInsetPx = spacing.xl;
const statusPieMinimumChartHeight = 96;
const statusPieMinimumVisualSize = 1;
const statusPieMinimumOuterRadius = 48;
const statusPieMinimumInnerRadius = 30;
const statusPieOuterRadiusRatio = 0.47;

type StatusPieChartSlotSize = {
  height: number;
  width: number;
};

export const StatusPieBreakdownPanel = ({
  loading,
  showPlaceholder = false,
  showErrorState,
  piePanelHeight,
  pieChartVisualHeight,
  miniPanels,
  headerToChartGap,
  miniMetricEmptyTitle: _miniMetricEmptyTitle,
  statusPieData,
  statusLegend,
  legendMarginTop = s('none'),
}: StatusPieBreakdownPanelProps): ReactElement => {
  const chartSlotRef = useRef<HTMLDivElement | null>(null);
  const fallbackChartSize = Math.min(pieChartVisualHeight, statusPieFallbackChartHeight);
  const [chartSlotSize, setChartSlotSize] = useState<StatusPieChartSlotSize>({
    height: fallbackChartSize,
    width: fallbackChartSize,
  });
  const runCount = statusPieData.reduce((sum, entry) => sum + entry.value, 0);
  const formattedRunCount = runCountFormatter.format(runCount);
  const runCountLabel = runCount === 1 ? 'Run' : 'Runs';
  const legendRowCount = statusLegend ? statusPieData.length : 0;
  const statusLegendSlotHeight =
    legendRowCount > 0
      ? legendRowCount * statusLegendRowHeightPx +
        Math.max(0, legendRowCount - 1) * statusLegendRowGapPx
      : 0;
  const effectivePieChartVisualHeight = Math.max(
    statusPieMinimumChartHeight,
    Math.floor(chartSlotSize.height),
  );
  const effectivePieChartVisualSize = Math.max(
    statusPieMinimumVisualSize,
    Math.floor(
      Math.min(
        chartSlotSize.height,
        Math.max(statusPieMinimumVisualSize, chartSlotSize.width - statusPieHorizontalInsetPx * 2),
      ),
    ),
  );
  const statusPieMaximumOuterRadius = Math.max(
    statusPieMinimumVisualSize,
    Math.floor(effectivePieChartVisualSize / 2) - 1,
  );
  const statusPieOuterRadius = Math.min(
    statusPieMaximumOuterRadius,
    Math.max(
      statusPieMinimumOuterRadius,
      Math.floor(effectivePieChartVisualSize * statusPieOuterRadiusRatio),
    ),
  );
  const statusPieInnerRadius = Math.min(
    Math.max(statusPieMinimumVisualSize, statusPieOuterRadius - 1),
    Math.max(statusPieMinimumInnerRadius, Math.floor(statusPieOuterRadius * 0.56)),
  );

  useEffect(() => {
    const element = chartSlotRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    let rafId = 0;
    const measure = (): void => {
      const bounds = element.getBoundingClientRect();
      const nextHeight = Math.floor(bounds.height);
      const nextWidth = Math.floor(bounds.width);
      if (
        !Number.isFinite(nextHeight) ||
        !Number.isFinite(nextWidth) ||
        nextHeight <= 0 ||
        nextWidth <= 0
      ) {
        return;
      }
      setChartSlotSize((currentSize) =>
        Math.abs(currentSize.height - nextHeight) >= 1 ||
        Math.abs(currentSize.width - nextWidth) >= 1
          ? { height: nextHeight, width: nextWidth }
          : currentSize,
      );
    };
    const schedule = (): void => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(measure);
    };
    schedule();
    const observer = new ResizeObserver(() => {
      schedule();
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    const nextFallbackChartSize = Math.min(pieChartVisualHeight, statusPieFallbackChartHeight);
    setChartSlotSize({ height: nextFallbackChartSize, width: nextFallbackChartSize });
  }, [pieChartVisualHeight]);

  return (
    <MetricsChartPanelFrame
      height={piePanelHeight}
      header={
        <ChartPanelTitle
          title={getChartTitle('statusBreakdown')}
          component="h2"
          fontSizePx={17}
          justifyContent="center"
          showInfoIcon={false}
        />
      }
      headerPaddingX={s('xl')}
      headerPaddingTop={s('lg')}
      headerPaddingBottom={s('xs')}
      contentPaddingX={s('lg')}
      contentPaddingTop={s('none')}
      contentPaddingBottom={s('xl')}
      headerSx={{
        minHeight: `${miniPanels.headerHeight}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      contentSx={{
        display: 'grid',
        alignItems: 'stretch',
      }}
    >
      {showPlaceholder || loading ? (
        renderStatusPieLoadingState(effectivePieChartVisualHeight)
      ) : showErrorState ? (
        <Box
          sx={{
            width: '100%',
            height: effectivePieChartVisualHeight,
            minHeight: effectivePieChartVisualHeight,
            display: 'grid',
            placeItems: 'center',
            transform: `translateY(${headerToChartGap}px)`,
          }}
        >
          <StatusBanner
            tone="red"
            variant="outlined"
            message={getErrorCopy('banner.chartLoadFailed')}
            sx={chartLoadErrorBannerSx}
          />
        </Box>
      ) : statusPieData.length > 0 ? (
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            height: '100%',
            minHeight: 0,
          }}
        >
          <Box
            ref={chartSlotRef}
            sx={{
              display: 'grid',
              flex: '1 1 auto',
              placeItems: 'center',
              minHeight: 0,
              overflow: 'hidden',
              position: 'relative',
              width: '100%',
            }}
          >
            <Box
              sx={{
                height: `${effectivePieChartVisualSize}px`,
                maxWidth: '100%',
                position: 'relative',
                width: `${effectivePieChartVisualSize}px`,
              }}
            >
              <PieChart
                data={statusPieData}
                height={effectivePieChartVisualSize}
                innerRadius={statusPieInnerRadius}
                outerRadius={statusPieOuterRadius}
                showLegend={false}
                width={effectivePieChartVisualSize}
              />
              <Box
                aria-label={`${formattedRunCount} ${runCountLabel.toLowerCase()}`}
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  pointerEvents: 'none',
                  transform: `translateY(${statusPieCentreLabelOffsetY})`,
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    justifyItems: 'center',
                    gap: s('xxs'),
                  }}
                >
                  <Text
                    role="sectionTitle"
                    component="span"
                    align="center"
                    sx={{
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1,
                    }}
                  >
                    {formattedRunCount}
                  </Text>
                  <Text
                    role="bodyStrong"
                    color="textSecondary"
                    component="span"
                    align="center"
                    sx={{
                      lineHeight: 1.2,
                    }}
                  >
                    {runCountLabel}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: '0 0 auto',
              minHeight: `${statusLegendSlotHeight}px`,
              mt: legendMarginTop,
              alignItems: 'flex-end',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            {statusLegend}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: '100%',
            height: pieChartVisualHeight,
            minHeight: pieChartVisualHeight,
            display: 'grid',
            placeItems: 'center',
            transform: `translateY(${headerToChartGap}px)`,
          }}
        >
          <ChartEmptyState title={CHART_EMPTY_STATE_NO_DATA_LABEL} variant="metricCard" />
        </Box>
      )}
    </MetricsChartPanelFrame>
  );
};
